import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const HOLDINGS_COLUMNS_STORAGE_KEY = 'holdingsTableVisibleColumns';
const CLOSED_HOLDINGS_COLUMNS_STORAGE_KEY = 'closedHoldingsTableVisibleColumns';

const HOLDINGS_COLUMN_OPTIONS = [
  { id: 'image', label: 'Image' },
  { id: 'name', label: 'Name' },
  { id: 'type', label: 'Type' },
  { id: 'ticker', label: 'Ticker' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'weight', label: 'Weight' },
  { id: 'avgBuy', label: 'Avg Buy' },
  { id: 'avgSell', label: 'Avg Sell' },
  { id: 'costBasis', label: 'Cost Basis' },
  { id: 'costBasisPortfolio', label: 'Cost Basis (Portfolio)' },
  { id: 'currentPrice', label: 'Current Price' },
  { id: 'currentValue', label: 'Current Value' },
  { id: 'currentValuePortfolio', label: 'Current Value (Portfolio)' },
  { id: 'pe', label: 'PE' },
  { id: 'dividendYield', label: 'Dividend Yield' },
  { id: 'annualDividendPerShare', label: 'Annual Dividend / Share' },
  { id: 'annualDividendTotal', label: 'Annual Dividend Total' },
  { id: 'dividendFrequency', label: 'Div. Frequency' },
  { id: 'dividendPerPayment', label: 'Div. / Payment' },
  { id: 'yieldOnCost', label: 'Yield on Cost' },
  { id: 'plLocal', label: 'P/L' },
  { id: 'plPortfolio', label: 'P/L (Portfolio)' },
  { id: 'realizedPlLocal', label: 'Realized P/L (Local)' },
  { id: 'realizedPlPortfolio', label: 'Realized P/L (Portfolio)' },
  { id: 'totalPlPortfolio', label: 'Total P/L (Portfolio)' },
  { id: 'transactions', label: 'Transactions' },
];

const DEFAULT_VISIBLE_COLUMNS = [
  'image',
  'name',
  'ticker',
  'quantity',
  'weight',
  'avgBuy',
  'currentPrice',
  'currentValue',
  'currentValuePortfolio',
  'plLocal',
  'plPortfolio',
  'transactions',
];

const DEFAULT_CLOSED_VISIBLE_COLUMNS = [
  'image',
  'name',
  'type',
  'ticker',
  'quantity',
  'avgBuy',
  'avgSell',
  'currentPrice',
  'currentValue',
  'currentValuePortfolio',
  'realizedPlLocal',
  'realizedPlPortfolio',
  'totalPlPortfolio',
  'transactions',
];

function HoldingsTable({
  loading,
  groupedHoldings,
  portfolioId,
  portfolioSlug,
  reportCurrency,
  isTransactionMode = false,
  title = 'My Holdings',
  isClosed = false,
  // Optional overrides for callers (e.g. demo portfolios) that want a
  // tailored column set without sharing localStorage with the real dashboard.
  defaultVisibleColumns = null,
  storageKey: storageKeyOverride,
}) {
  const storageKey = storageKeyOverride !== undefined
    ? storageKeyOverride
    : (isClosed ? CLOSED_HOLDINGS_COLUMNS_STORAGE_KEY : HOLDINGS_COLUMNS_STORAGE_KEY);
  const defaultCols = defaultVisibleColumns
    || (isClosed ? DEFAULT_CLOSED_VISIBLE_COLUMNS : DEFAULT_VISIBLE_COLUMNS);

  const safeHoldings = Array.isArray(groupedHoldings) ? groupedHoldings : [];
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    if (!storageKey) return defaultCols;
    try {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored ? JSON.parse(stored) : null;

      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch {
      // ignore storage parsing issues
    }

    return defaultCols;
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    } catch {
      // ignore storage write issues
    }
  }, [visibleColumns, storageKey]);

  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);

  const toggleColumn = (columnId) => {
    setVisibleColumns((prev) => {
      const exists = prev.includes(columnId);

      if (exists) {
        if (prev.length === 1) {
          return prev;
        }

        return prev.filter((id) => id !== columnId);
      }

      return [...prev, columnId];
    });
  };

  const resetColumns = () => {
    setVisibleColumns(defaultCols);
  };

  const orderedVisibleColumns = useMemo(() => {
    return HOLDINGS_COLUMN_OPTIONS.map((item) => item.id).filter((id) =>
      visibleColumnSet.has(id)
    );
  }, [visibleColumnSet]);

  // Columns that freeze when scrolling horizontally (identity columns, in order)
  const STICKY_WIDTHS = { image: 64, name: 180 };

  const getStickyStyle = (columnId, isHeader = false) => {
    const stickyIds = Object.keys(STICKY_WIDTHS);
    const visibleSticky = orderedVisibleColumns.filter((id) => stickyIds.includes(id));
    const myIndex = visibleSticky.indexOf(columnId);
    if (myIndex === -1) return null;
    let left = 0;
    for (let i = 0; i < myIndex; i++) {
      left += STICKY_WIDTHS[visibleSticky[i]];
    }
    return {
      position: 'sticky',
      left,
      zIndex: isHeader ? 3 : 1,
      background: 'var(--surface-strong)',
    };
  };

  const getColumnLabel = (id) => {
    if (id === 'costBasisPortfolio') {
      return `Cost Basis ( ${reportCurrency} )`;
    }

    if (id === 'currentValuePortfolio') {
      return `Current Value ( ${reportCurrency} )`;
    }

    if (id === 'plPortfolio') {
      return `P/L ( ${reportCurrency} )`;
    }

    if (id === 'realizedPlLocal') {
      return 'Realized P/L (Local)';
    }

    if (id === 'realizedPlPortfolio') {
      return `Realized P/L ( ${reportCurrency} )`;
    }

    if (id === 'totalPlPortfolio') {
      return `Total P/L ( ${reportCurrency} )`;
    }

    if (id === 'annualDividendTotal') {
      return `Annual Dividend Total ( ${reportCurrency} )`;
    }

    return HOLDINGS_COLUMN_OPTIONS.find((item) => item.id === id)?.label || id;
  };

  return (
    <section style={cardStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>{title}</h3>

        <div style={headerActionsStyle}>
          <button
            type="button"
            onClick={() => setShowColumnSettings((prev) => !prev)}
            style={settingsButtonStyle}
          >
            {showColumnSettings ? 'Close columns' : 'Customize columns'}
          </button>
        </div>
      </div>

      {showColumnSettings ? (
        <div style={settingsCardStyle}>
          <div style={settingsHeaderStyle}>
            <div>
              <h4 style={settingsTitleStyle}>Visible columns</h4>
              <p style={settingsSubtitleStyle}>
                Choose which columns you want to display in the holdings table.
              </p>
            </div>

            <button type="button" onClick={resetColumns} style={resetButtonStyle}>
              Reset to default
            </button>
          </div>

          <div style={settingsInfoBoxStyle}>
            Action stays fixed at the end. Image and Name are separate.
          </div>

          <div style={settingsGridStyle}>
            {HOLDINGS_COLUMN_OPTIONS.map((column) => (
              <label key={column.id} style={settingOptionStyle}>
                <input
                  type="checkbox"
                  checked={visibleColumnSet.has(column.id)}
                  onChange={() => toggleColumn(column.id)}
                />
                <span>{getColumnLabel(column.id)}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {loading ? (
        <p style={textStyle}>Loading data...</p>
      ) : safeHoldings.length === 0 ? (
        <p style={textStyle}>No assets yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {orderedVisibleColumns.map((columnId) => {
                  const stickyStyle = getStickyStyle(columnId, true);
                  return (
                    <th
                      key={columnId}
                      style={stickyStyle ? { ...thStyle, ...stickyStyle } : thStyle}
                    >
                      {getColumnLabel(columnId)}
                    </th>
                  );
                })}
                <th style={thStyle}>Action</th>
              </tr>
            </thead>

            <tbody>
              {safeHoldings.map((holding) => {
                const positionsCount = isTransactionMode
                  ? Number(holding.buyTransactions || 0) + Number(holding.sellTransactions || 0)
                  : typeof holding.positionsCount === 'number'
                    ? holding.positionsCount
                    : Array.isArray(holding.positions)
                      ? holding.positions.length
                      : 0;

                const DUST = 1e-6;
                const rawQty = isClosed
                  ? Number(holding.soldQuantity || 0)
                  : isTransactionMode
                    ? Number(holding.openQuantity || 0)
                    : Number(holding.totalQuantity || 0);
                const quantity = rawQty < DUST ? 0 : rawQty;

                const assetCurrency = holding.currency || reportCurrency || 'USD';
                const tickerValue = (holding.ticker || '').toUpperCase();

                const averageBuy = isTransactionMode
                  ? Number(holding.averageCostPerUnit || 0)
                  : Number(holding.averageBuyPrice || 0);

                const costBasisNative = isTransactionMode
                  ? Number(holding.costBasisOpenNative || 0)
                  : 0;

                const costBasisBase = isTransactionMode
                  ? Number(holding.costBasisOpenBase || 0)
                  : 0;

                const currentPrice = Number(holding.currentPrice || 0);

                const currentValueNative = isTransactionMode
                  ? Number(holding.marketValueNative || 0)
                  : Number(holding.currentValue || 0);

                const currentValueBase = isTransactionMode
                  ? Number(holding.marketValueBase || 0)
                  : Number(holding.currentValue || 0);

                const profitValueNative = isTransactionMode
                  ? Number(holding.unrealizedPnlNative || 0)
                  : Number(holding.profitLoss || 0);

                const profitValueBase = isTransactionMode
                  ? Number(holding.unrealizedPnlBase || 0)
                  : Number(holding.profitLoss || 0);

                const realizedPnlBase = Number(holding.realizedPnlBase || 0);
                const totalPnlBase = Number(holding.totalPnlBase || 0);

                const profitPercent = isTransactionMode
                  ? Number(holding.unrealizedPnlPercent || 0)
                  : Number(holding.profitLossPercent || 0);

                const peRatio = holding.peRatio ?? null;
                const dividendYield = holding.dividendYield ?? null;
                const annualDividendPerShare = holding.annualDividendPerShare ?? null;
                const annualDividendTotal = holding.annualDividendTotal ?? null;
                const dividendFrequency = holding.dividendFrequency ?? null;
                const dividendPerPayment = holding.dividendPerPayment ?? null;
                const yieldOnCost = holding.yieldOnCost ?? null;

                const profitColor =
                  profitValueNative > 0
                    ? 'var(--success)'
                    : profitValueNative < 0
                      ? 'var(--danger)'
                      : 'var(--text-primary)';

                const baseProfitColor =
                  profitValueBase > 0
                    ? 'var(--success)'
                    : profitValueBase < 0
                      ? 'var(--danger)'
                      : 'var(--text-primary)';

                const realizedProfitColor =
                  realizedPnlBase > 0
                    ? 'var(--success)'
                    : realizedPnlBase < 0
                      ? 'var(--danger)'
                      : 'var(--text-primary)';

                const totalProfitColor =
                  totalPnlBase > 0
                    ? 'var(--success)'
                    : totalPnlBase < 0
                      ? 'var(--danger)'
                      : 'var(--text-primary)';

                return (
                  <tr key={holding.instrumentId || holding.assetType + '-' + tickerValue}>
                    {orderedVisibleColumns.map((columnId) => {
                      const stickyTd = getStickyStyle(columnId, false);
                      switch (columnId) {
                        case 'image':
                          return (
                            <td key={columnId} style={{ ...tdStyle, width: '48px', ...(stickyTd || {}) }}>
                              <div style={imageCellStyle}>
                                <AssetBadge
                                  ticker={tickerValue}
                                  name={holding.displayName || tickerValue}
                                  logoUrl={holding.logoUrl}
                                  assetType={holding.assetType}
                                />
                              </div>
                            </td>
                          );

                        case 'name':
                          return (
                            <td key={columnId} style={{ ...tdStyle, minWidth: '140px', ...(stickyTd || {}) }}>
                              <div style={nameOnlyCellStyle}>
                                {holding.displayName || tickerValue}
                              </div>
                            </td>
                          );

                        case 'type':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {holding.assetType}
                            </td>
                          );

                        case 'ticker':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {tickerValue}
                            </td>
                          );

                        case 'quantity':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatNumber(quantity)}
                            </td>
                          );

                        case 'weight':
                          return (
                            <td key={columnId} style={{ ...tdStyle, fontWeight: 700 }}>
                              {Number(holding.portfolioSharePercent || 0).toFixed(2)}%
                            </td>
                          );

                        case 'avgBuy':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatCurrency(averageBuy, assetCurrency)}
                            </td>
                          );

                        case 'avgSell': {
                          const avgSellVal = Number(holding.averageSellPrice || 0);
                          return (
                            <td key={columnId} style={tdStyle}>
                              {avgSellVal > 0 ? formatCurrency(avgSellVal, assetCurrency) : '-'}
                            </td>
                          );
                        }

                        case 'costBasis':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatCurrency(costBasisNative, assetCurrency)}
                            </td>
                          );

                        case 'costBasisPortfolio':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatCurrency(costBasisBase, reportCurrency)}
                            </td>
                          );

                        case 'currentPrice':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {currentPrice > 0
                                ? formatCurrency(currentPrice, assetCurrency)
                                : '-'}
                            </td>
                          );

                        case 'currentValue':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatCurrency(currentValueNative, assetCurrency)}
                            </td>
                          );

                        case 'currentValuePortfolio':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {formatCurrency(currentValueBase, reportCurrency)}
                            </td>
                          );

                        case 'pe':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {peRatio !== null ? Number(peRatio).toFixed(2) : '-'}
                            </td>
                          );

                        case 'dividendYield':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {dividendYield !== null ? `${Number(dividendYield).toFixed(2)}%` : '-'}
                            </td>
                          );

                        case 'annualDividendPerShare':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {annualDividendPerShare !== null
                                ? formatCurrency(Number(annualDividendPerShare), assetCurrency)
                                : '-'}
                            </td>
                          );

                        case 'annualDividendTotal':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {annualDividendTotal !== null
                                ? formatCurrency(Number(annualDividendTotal), assetCurrency)
                                : '-'}
                            </td>
                          );

                        case 'dividendFrequency':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {dividendFrequency
                                ? (dividendFrequency === 1 ? 'Annual'
                                  : dividendFrequency === 2 ? 'Semi-annual'
                                  : dividendFrequency === 4 ? 'Quarterly'
                                  : dividendFrequency === 12 ? 'Monthly'
                                  : `${dividendFrequency} / yr`)
                                : '-'}
                            </td>
                          );

                        case 'dividendPerPayment':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {dividendPerPayment !== null
                                ? formatCurrency(Number(dividendPerPayment), assetCurrency)
                                : '-'}
                            </td>
                          );

                        case 'yieldOnCost':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {yieldOnCost !== null ? `${Number(yieldOnCost).toFixed(2)}%` : '-'}
                            </td>
                          );

                        case 'plLocal':
                          return (
                            <td
                              key={columnId}
                              style={{ ...tdStyle, color: profitColor, fontWeight: 700 }}
                            >
                              <div>{formatSignedCurrency(profitValueNative, assetCurrency)}</div>
                              <div style={{ fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>
                                {profitPercent >= 0 ? '+' : ''}
                                {profitPercent.toFixed(2)}%
                              </div>
                            </td>
                          );

                        case 'plPortfolio':
                          return (
                            <td
                              key={columnId}
                              style={{ ...tdStyle, color: baseProfitColor, fontWeight: 700 }}
                            >
                              {formatSignedCurrency(profitValueBase, reportCurrency)}
                            </td>
                          );

                        case 'realizedPlLocal': {
                          const rlNative = Number(holding.realizedPnlNative || 0);
                          const rlColor = rlNative > 0 ? 'var(--success)' : rlNative < 0 ? 'var(--danger)' : 'var(--text-primary)';
                          return (
                            <td key={columnId} style={{ ...tdStyle, color: rlColor, fontWeight: 700 }}>
                              {formatSignedCurrency(rlNative, assetCurrency)}
                            </td>
                          );
                        }

                        case 'realizedPlPortfolio':
                          return (
                            <td
                              key={columnId}
                              style={{ ...tdStyle, color: realizedProfitColor, fontWeight: 700 }}
                            >
                              {formatSignedCurrency(realizedPnlBase, reportCurrency)}
                            </td>
                          );

                        case 'totalPlPortfolio':
                          return (
                            <td
                              key={columnId}
                              style={{ ...tdStyle, color: totalProfitColor, fontWeight: 700 }}
                            >
                              {formatSignedCurrency(totalPnlBase, reportCurrency)}
                            </td>
                          );

                        case 'transactions':
                          return (
                            <td key={columnId} style={tdStyle}>
                              {positionsCount}
                            </td>
                          );

                        default:
                          return (
                            <td key={columnId} style={tdStyle}>
                              -
                            </td>
                          );
                      }
                    })}

                    <td style={tdStyle}>
                      <Link
                        to={'/portfolio/' + (portfolioSlug || portfolioId) + '/' + encodeURIComponent(tickerValue)}
                        style={editLinkStyle}
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AssetBadge({ ticker, name, logoUrl, assetType }) {
  const [imageError, setImageError] = useState(false);
  const fallbackText = String(ticker || name || '?').slice(0, 1).toUpperCase();

  // Compute a logo URL: prefer backend-provided one, otherwise derive from ticker.
  // Logo.dev works for any equity/ETF/crypto symbol — strip the -USD suffix and any
  // numeric Yahoo disambiguator (BTC-USD → BTC, TON11419-USD → TON).
  const resolvedUrl = (() => {
    if (logoUrl) return logoUrl;
    if (!ticker) return null;
    const symbol = String(ticker)
      .toUpperCase()
      .replace(/-USD$/, '')
      .replace(/\d+$/, '')
      .replace('-', '.');
    if (!symbol) return null;
    return `https://img.logo.dev/ticker/${symbol}?token=pk_YJM_LolkQ9WfHMCBGBwlog&size=64`;
  })();

  if (resolvedUrl && !imageError) {
    const isCrypto = assetType === 'Crypto';
    return (
      <div style={isCrypto ? { ...assetBadgeStyle, background: 'rgba(245,158,11,0.10)' } : assetBadgeStyle}>
        <img
          src={resolvedUrl}
          alt={name || ticker || 'Asset'}
          style={assetLogoStyle}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return <div style={assetBadgeFallbackStyle}>{fallbackText}</div>;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 8,
  }).format(Number(value || 0));
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

function formatSignedCurrency(value, currency) {
  const numberValue = Number(value || 0);
  const formatted = formatCurrency(Math.abs(numberValue), currency);

  if (numberValue > 0) return '+' + formatted;
  if (numberValue < 0) return '-' + formatted;
  return formatted;
}

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const titleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
};

const headerActionsStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
};

const settingsButtonStyle = {
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '10px 14px',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontWeight: 700,
};

const settingsCardStyle = {
  marginBottom: '18px',
  padding: '16px',
  borderRadius: '14px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  display: 'grid',
  gap: '14px',
};

const settingsHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
  flexWrap: 'wrap',
};

const settingsTitleStyle = {
  margin: 0,
  marginBottom: '6px',
  color: 'var(--text-primary)',
};

const settingsSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const settingsInfoBoxStyle = {
  padding: '10px 12px',
  borderRadius: '10px',
  background: 'rgba(139, 124, 255, 0.10)',
  border: '1px solid rgba(139, 124, 255, 0.25)',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: '13px',
};

const settingsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: '12px',
};

const settingOptionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
  fontWeight: 600,
};

const resetButtonStyle = {
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '10px 14px',
  background: 'var(--surface-strong)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontWeight: 700,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '12px',
  color: 'var(--text-primary)',
  fontSize: '13px',
};

const textStyle = {
  color: 'var(--text-primary)',
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid var(--table-border)',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  letterSpacing: '0.3px',
};

const tdStyle = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--table-row-border)',
  verticalAlign: 'middle',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const imageCellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const nameOnlyCellStyle = {
  color: 'var(--text-primary)',
  fontWeight: 700,
  lineHeight: 1.25,
  minWidth: '140px',
};

const assetBadgeStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
};

const assetLogoStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const assetBadgeFallbackStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--primary)',
  color: '#ffffff',
  fontWeight: 800,
  fontSize: '14px',
  border: '1px solid rgba(255,255,255,0.08)',
};

const editLinkStyle = {
  display: 'inline-block',
  padding: '8px 12px',
  borderRadius: '8px',
  background: 'var(--primary)',
  color: '#ffffff',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

export default HoldingsTable;
